/**
 * WS-ABSENT (constitution closure 2026-10-20) — marking a technician absent asks a PERSON where each ticket goes.
 *
 * Law: CORE-AB:43 "SHALL NOT assign work autonomously … The final selection is a human act, and where an automatic
 * path exists it MUST be something the shop explicitly chose"; CORE-AD:80 "No automation may rearrange the board
 * unattended unless the shop has explicitly enabled an optimisation path"; ledger FD-S3 (human-governed assignment is
 * the fallback; ranking and recommendation are preserved).
 *
 * Before: the Dashboard committed smart-assign's #1 for every ticket of the absent technician the moment the badge was
 * toggled ('absent-tech-reassign', recorded automatic), and parked whatever it could not place. Now nothing moves until
 * the person confirms a row. smart-assign still ranks (its hard constraints filter who is eligible); the ranking is
 * SHOWN, never preselected — every row starts on "leave with <technician>". A confirmed move is the person's choice and
 * goes through reassign-ticket with the human reasons the ticket kebab uses ('manual-kebab-tech-change' /
 * 'manual-kebab-to-parking-lot'), so it records `manual`.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';

export interface AbsentReviewRanking {
  technicianId: string;
  technicianName?: string;
  suggestedStartTime?: string | null;
}

/** What the person chose for one ticket. 'keep' (the default) moves nothing. */
export type AbsentReviewChoice = 'keep' | 'parking' | { techId: string };

/** The reassign-ticket body a confirmed choice sends — null for 'keep'. Human reasons only. */
export function absentReviewMoveBody(
  ticketId: string,
  scheduledDate: string,
  choice: AbsentReviewChoice,
): Record<string, unknown> | null {
  if (choice === 'keep') return null;
  if (choice === 'parking') {
    return {
      ticketId,
      targetTechId: null,
      scheduledDate,
      moveToParkingLot: true,
      parkingLotReason: 'other',
      moveReason: 'manual-kebab-to-parking-lot',
    };
  }
  return {
    ticketId,
    targetTechId: choice.techId,
    scheduledDate,
    manualOverride: true,
    moveReason: 'manual-kebab-tech-change',
  };
}

interface Row {
  rankings: AbsentReviewRanking[];
  loading: boolean;
  choice: string; // 'keep' | 'parking' | `tech:<id>`
  done: string | null;
  busy: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  technicianName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tickets: any[];
  shopId: string;
  selectedDayLabel: string;
  shopTimezone: string;
}

const toChoice = (v: string): AbsentReviewChoice =>
  v === 'keep' || v === 'parking' ? v : { techId: v.slice('tech:'.length) };

export function AbsentTechReassignReview({ open, onClose, technicianName, tickets, shopId, selectedDayLabel }: Props) {
  const { toast } = useToast();
  const reviewable = tickets.filter((t) => !t.parking_lot);
  const [rows, setRows] = useState<Record<string, Row>>({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const initial: Record<string, Row> = {};
    for (const t of reviewable) initial[t.id] = { rankings: [], loading: true, choice: 'keep', done: null, busy: false };
    setRows(initial);
    // Ranking only: smart-assign is asked for its ranked options (no pick is requested and nothing is committed).
    for (const t of reviewable) {
      const jobs = (t.jobs || []).map((job: Record<string, any>) => ({
        id: job.id,
        name: job.name,
        category: job.category,
        proficiency_category: job.proficiency_category || job.category,
        skill: job.skill_level || job.skillLevel || 3,
        minutes: job.standard_minutes || job.standardMinutes || 30,
        bay: job.bay_requirement || job.bayRequirement || 'Flat',
      }));
      void supabase.functions.invoke('smart-assign', {
        body: {
          ticketData: {
            shop_id: shopId,
            type: t.type || 'Drop-off',
            jobs,
            predictedMinutes: t.predicted_minutes ?? t.predictedMinutes
              ?? jobs.reduce((s: number, j: { minutes: number }) => s + (Number(j.minutes) || 0), 0),
            appointmentDate: t.scheduled_date || selectedDayLabel,
            vehicleInfo: t.vehicle_info || t.vehicleInfo,
          },
          skipSplitCheck: true,
        },
      }).then(({ data, error }) => {
        if (cancelled) return;
        const ranked = (error ? [] : (data?.data?.rankings ?? data?.rankings ?? [])) as AbsentReviewRanking[];
        const absentId = t.assigned_tech_id ?? t.assignedTechId;
        setRows((prev) => ({
          ...prev,
          [t.id]: { ...prev[t.id], loading: false, rankings: ranked.filter((r) => r.technicianId && r.technicianId !== absentId) },
        }));
      });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setRow = (id: string, patch: Partial<Row>) => setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const confirm = async (t: { id: string; scheduled_date?: string }) => {
    const row = rows[t.id];
    if (!row) return;
    const scheduledDate = t.scheduled_date || selectedDayLabel;
    const body = absentReviewMoveBody(t.id, scheduledDate, toChoice(row.choice));
    if (!body) return;
    setRow(t.id, { busy: true });
    const { error } = await supabase.functions.invoke('reassign-ticket', { body });
    if (error) {
      setRow(t.id, { busy: false });
      toast({ title: 'Move not made', description: error.message || 'The ticket stays where it is.', variant: 'destructive' });
      return;
    }
    const target = row.choice === 'parking'
      ? 'parking lot'
      : row.rankings.find((r) => `tech:${r.technicianId}` === row.choice)?.technicianName ?? 'the chosen technician';
    setRow(t.id, { busy: false, done: `Moved to ${target}` });
    void notifyDayboardInvalidated({
      reason: row.choice === 'parking' ? 'kebab-parking-lot' : 'kebab-reassign',
      shopId,
      ticketId: t.id,
      scheduledDate,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{technicianName} is absent — review their tickets</DialogTitle>
          <DialogDescription>
            Nothing has been moved. Choose where each ticket goes and confirm it; OptiBay&apos;s ranked suggestions are
            listed for you to pick from.
          </DialogDescription>
        </DialogHeader>
        {reviewable.length === 0 ? (
          <p className="text-sm text-muted-foreground">No scheduled tickets to review.</p>
        ) : (
          <ul className="space-y-3">
            {reviewable.map((t) => {
              const row = rows[t.id];
              const label = t.customer_name || t.customerName || `Ticket ${t.simple_ticket_number ?? t.id}`;
              const selectId = `absent-review-${t.id}`;
              return (
                <li key={t.id} className="rounded-md border p-3 space-y-2" data-testid={`absent-review-row-${t.id}`}>
                  <label htmlFor={selectId} className="text-sm font-medium block">{label}</label>
                  {row?.done ? (
                    <p className="text-sm text-muted-foreground">{row.done}</p>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <select
                        id={selectId}
                        className="flex-1 min-w-0 rounded-md border bg-background px-2 py-1 text-sm"
                        value={row?.choice ?? 'keep'}
                        disabled={!row || row.busy}
                        onChange={(e) => setRow(t.id, { choice: e.target.value })}
                      >
                        <option value="keep">Leave with {technicianName}</option>
                        {(row?.rankings ?? []).map((r, i) => (
                          <option key={r.technicianId} value={`tech:${r.technicianId}`}>
                            {/* No start time is shown: reassign-ticket takes none and the server places the work, so a
                                displayed time would not be what the person approves (founder review 2026-10-20). */}
                            {i === 0 ? 'Suggested: ' : ''}{r.technicianName ?? r.technicianId}
                          </option>
                        ))}
                        <option value="parking">Send to parking lot</option>
                      </select>
                      <Button
                        size="sm"
                        disabled={!row || row.busy || row.choice === 'keep'}
                        onClick={() => void confirm(t)}
                      >
                        Confirm
                      </Button>
                    </div>
                  )}
                  {row?.loading ? <p className="text-xs text-muted-foreground">Finding suggestions…</p> : null}
                  {row && !row.loading && !row.done && row.rankings.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No other technician can take this ticket right now.</p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
